/** @file The rounded points of one node a drawable reads: drawable point A, node N, bone origin O (21.4). */
import type { Quat } from '@zilo/pose3d-math/quat/quat';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import type { PoseBuffer } from '@zilo/pose3d-pose/contract';
import { rotateZScaled } from '@zilo/pose3d-pose/chain/rotateZScaled';
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';
import type { PartMap } from '@zilo/pose3d-render-core/place/partMapOf';
import { poseNodeOf } from '@zilo/pose3d-render-core/place/poseNodeOf';
import { snapRelOf } from '@zilo/pose3d-render-core/place/snapRelOf';
import { worldPointOf } from '@zilo/pose3d-render-core/place/worldPointOf';

/** The host part map of the drawable and the name of the node whose points are read (22.3). */
export type NodeParts = { map: PartMap; name: string };

/**
 * Entity-relative points of a node, each rounded once; `root` when it has no effective parent (no bone, 21.3);
 * `parts` the host part map the second points read (anchor offsets are moved once, 22.3).
 */
export type NodePoints = { a: Vec3; n: Vec3; o: Vec3; root: boolean; q: Quat; scale: number; parts?: NodeParts };

/** `M(back(N) + Zs(q, s offset))`: A from the node's own (unmoved) coordinates, moved once by the drawable's part. */
function movedPointOf(pose: PoseBuffer, nodeIndex: number, offset: Vec3, zScaleRotate: number, parts: NodeParts): Vec3 {
  const { pos, q, scale } = poseNodeOf(pose, nodeIndex);
  const base = parts.map.back(pos, parts.name);
  const r = rotateZScaled(q, [offset[0] * scale, offset[1] * scale, offset[2] * scale], zScaleRotate);
  return parts.map.move([base[0] + r[0], base[1] + r[1], base[2] + r[2]]);
}

/**
 * `a = round(N + Zs(q, s offset))` from the **unrounded** N (21.4 rule 2), `n = round(N)`, `o = round(O)` with O the
 * pose's `ox oy oz` columns (rule 1); no rounding without `snap 'pixel'`. With `parts` (22.3) N and O are as pose moved
 * them, and A is `round(M(back(N) + Zs(q, s offset)))`: built in the node's own coordinates, moved once, then rounded.
 */
export function nodePointsOf(pose: PoseBuffer, nodeIndex: number, offset: Vec3, view: View, zScaleRotate: number, parts?: NodeParts): NodePoints {
  const node = poseNodeOf(pose, nodeIndex);
  const a = parts === undefined ? worldPointOf(pose, nodeIndex, offset, zScaleRotate) : movedPointOf(pose, nodeIndex, offset, zScaleRotate, parts);
  return {
    a: snapRelOf(a, view), n: snapRelOf(node.pos, view), o: snapRelOf(node.origin, view), root: node.parentIndex < 0, q: node.q,
    scale: node.scale, ...(parts === undefined ? {} : { parts }),
  };
}
